import { Info, Lightbulb } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { HelpContent } from "@/content/help/types";
import { resolveHelpEntry } from "@/content/help/types";

interface FieldHintProps {
  content: HelpContent;
  stepId: string;
  fieldKey: string;
  overrideKey?: string | null;
}

/**
 * Split hint text into a lead sentence (shown emphasized) and the remaining
 * detail. Falls back to lead-only when the text is a single sentence.
 */
function splitLead(text: string): { lead: string; rest: string | null } {
  const match = text.match(/^(.+?[.!?])\s+(.+)$/s);
  if (!match) return { lead: text, rest: null };
  return { lead: match[1], rest: match[2] };
}

/**
 * Info-icon hint for a single field, sourced from the step's `fields` map.
 * Popover (not Tooltip) so tap-to-open works on touch devices.
 * Renders nothing when the field has no hint in the JSON.
 */
export function FieldHint({ content, stepId, fieldKey, overrideKey }: FieldHintProps) {
  const text = resolveHelpEntry(content, stepId, overrideKey)?.fields?.[fieldKey];
  if (!text) return null;

  const { lead, rest } = splitLead(text);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="About this field"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center align-middle ml-1 w-5 h-5 rounded-full text-sky-600 hover:text-sky-700 hover:bg-sky-100 transition-colors"
        >
          <Info className="w-4 h-4 fill-sky-100" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-0 overflow-hidden rounded-xl">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-sky-50 to-amber-50/60 border-b border-border/50">
          <Lightbulb className="w-3 h-3 text-amber-500 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider text-sky-700">Tip</span>
        </div>
        <div className="px-3 py-2.5">
          <p className="text-xs font-medium text-foreground leading-relaxed">{lead}</p>
          {rest && (
            <p className="text-xs text-muted-foreground leading-relaxed mt-1">{rest}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
